<template>
  <div class="pk-wrapper-container">
    <v-app>
      <div v-if="requiresAuth" class="pk-auth-wrapper">
        <!-- Saved Changes Alert -->
        <Alert />

        <Header
          :screen="screen"
          :stripeLastUpdated="stripeLastUpdated"
          @refresh="refetchStripeData"
          @refreshForce="refetchStripeData(false, true)" />

        <!-- STRIPE CONNECT SCREEN + STRIPE DOWNLOADING -->
        <NavWrapper v-if="screen === 'stripeConnect'
          || screen === 'stripeDownloading'">
          <!-- PROFILE -->
          <PageLoading v-if="routeName === 'profile'"
                       :loading="loading.profile || loading.paymentInfo"
                       pageName="Profile">
            <Profile
              :paymentInfo="paymentInfo"
              :profileData="profileData" />
          </PageLoading>


          <!-- INTEGRATIONS -->
          <PageLoading v-else-if="routeName === 'integrations'"
                       :loading="loading.integrations"
                       pageName="Integrations"
          >
            <Integrations :integrations="integrations" />
          </PageLoading>


          <!-- TEAM -->
          <PageLoading v-else-if="routeName === 'team'"
                       :loading="loading.team"
                       pageName="Team"
          >
            <Team
              :teamMembers="teamMembers" />
          </PageLoading>

          <!-- REFERRALS -->
          <PageLoading v-else-if="routeName === 'referrals'"
                       :loading="loading.referrals"
                       pageName="Referrals">
            <Referrals :referralsInfo="referrals" />
          </PageLoading>


          <!-- STRIPE CONNECT AUTH: SETUP -->
          <StripeAuth v-else-if="routeName === 'stripe-auth'"
                      @refresh="fetchData">
          </StripeAuth>

          <!-- STRIPE CONNECT AUTH: PROFILE -->
          <!--
          <StripeAuth
            :profile="true"
            @refresh="refetchStripeData"
            v-else-if="routeName === 'stripe-auth-profile'">
          </StripeAuth>
          -->


          <!-- GOOGLE CALENDAR AUTH OR GMAIL AUTH -->
          <PageLoading v-else-if="routeName === 'google-calendar-auth' || routeName === 'gmail-auth'"
                       :loading="loading.userInfo">
            <GoogleAuth v-if="userInfo && userInfo.user" :userId="userInfo.user.id" />
          </PageLoading>


          <div v-else>
            <StripeConnect v-if="screen === 'stripeConnect'" />
            <DownloadingStripeData v-else-if="screen === 'stripeDownloading'" />
            <router-view v-else />
          </div>
        </NavWrapper>

        <!-- START TRIAL + INVALID SUBSCRIPTION SCREEN -->
        <NavWrapper v-else-if="screen === 'startTrial'
          || screen === 'purchaseSubscription'">

          <!-- CALENDAR -->
          <PageLoading v-if="routeName === 'calendar'"
                       :loading="loading.calendar"
                       pageName="Calendar"
          >
            <CalendarView
              :calendarMetrics="calendarMetrics"
              :eventsAll="eventsAll"
              :eventsCancellation="eventsCancellation"
              :eventsDelinquent="eventsDelinquent"
              :eventsExpected="eventsExpected"
              :eventsPotential="eventsPotential"
            />
          </PageLoading>

          <!-- PROFILE -->
          <PageLoading v-else-if="routeName === 'profile'"
                       :loading="loading.profile || loading.paymentInfo"
                       pageName="Profile">
            <Profile
              :paymentInfo="paymentInfo"
              :profileData="profileData" />
          </PageLoading>


          <!-- INTEGRATIONS -->
          <PageLoading v-else-if="routeName === 'integrations'"
                       :loading="loading.integrations"
                       pageName="Integrations"
          >
            <Integrations :integrations="integrations" />
          </PageLoading>


          <!-- TEAM -->
          <PageLoading v-else-if="routeName === 'team'"
                       :loading="loading.team"
                       pageName="Team"
          >
            <Team
              :teamMembers="teamMembers" />
          </PageLoading>

          <!-- PAYMENT -->
          <PageLoading v-else-if="routeName === 'payment'"
                       :loading="loading.paymentInfo"
                       pageName="Payment">
            <Payment :paymentInfo="paymentInfo" />
          </PageLoading>

          <!-- REFERRALS -->
          <PageLoading v-else-if="routeName === 'referrals'"
                       :loading="loading.referrals"
                       pageName="Referrals">
            <Referrals :referralsInfo="referrals" />
          </PageLoading>

          <!-- STRIPE CONNECT AUTH: SETUP -->
          <StripeAuth v-else-if="routeName === 'stripe-auth'"
                      @refresh="fetchData">
          </StripeAuth>

          <!-- STRIPE CONNECT AUTH: PROFILE -->
          <!--
          <StripeAuth
            :profile="true"
            @refresh="refetchStripeData"
            v-else-if="routeName === 'stripe-auth-profile'">
          </StripeAuth>
          -->

          <!-- GOOGLE CALENDAR AUTH OR GMAIL AUTH -->
          <PageLoading v-else-if="routeName === 'google-calendar-auth' || routeName === 'gmail-auth'"
                       :loading="loading.userInfo">
            <GoogleAuth v-if="userInfo && userInfo.user" :userId="userInfo.user.id" />
          </PageLoading>

          <div v-else>
            <StartTrial v-if="screen === 'startTrial'"
                        :paymentInfo="paymentInfo"
                        @refresh="fetchData" />
            <Notice v-else-if="screen === 'purchaseSubscription'"
                    :paymentInfo="paymentInfo" />
            <router-view v-else />
          </div>
        </NavWrapper>

        <!-- REGULAR SCREEN -->
        <NavWrapper v-else>
          <!-- CALENDAR -->
          <PageLoading v-if="routeName === 'calendar'"
                       :loading="loading.calendar"
                       pageName="Calendar"
          >
            <CalendarView
              :calendarMetrics="calendarMetrics"
              :eventsAll="eventsAll"
              :eventsCancellation="eventsCancellation"
              :eventsDelinquent="eventsDelinquent"
              :eventsExpected="eventsExpected"
              :eventsPotential="eventsPotential"
            />
          </PageLoading>

          <PageLoading v-else-if="routeName === 'calendar-daily'"
                       :loading="loading.calendar"
                       pageName="Daily Calendar"
          >
            <CalendarDaily
              :events="calendarDailyData"
              :overallType="calendarDailyType"
            />
          </PageLoading>

          <PageLoading v-else-if="routeName === 'cashflow-charts'"
                       :loading="loading.calendar || loading.profile"
                       pageName="Cash Flow Charts"
          >
            <CashflowCharts :calendarMetrics="calendarMetrics" />
          </PageLoading>

          <!-- EXPORTS -->
          <PageLoading v-else-if="routeName === 'exports'"
                       :loading="loading.exports"
                       pageName="Exports"
          >
            <Exports :exports="exportEmails" />
          </PageLoading>

          <!-- UPCOMING EVENTS -->
          <PageLoading v-else-if="isCalendarEmailRoute"
                       :loading="loading.calendarEvents
              || loading.liveEvents
              || loading.pastEvents
              || loading.subscriptionProducts
              || loading.integrations"
                       pageName="Calendar Automated Emails">
            <CalendarEmails
              :calendarEvents="calendarEventsData"
              :pastEvents="pastEventsData"
            />
          </PageLoading>

          <!-- LIVE EVENTS -->
          <PageLoading v-else-if="routeName === 'liveEvents'"
                       :loading="loading.liveEvents"
                       pageName="Live Events">
            <LiveEvents />
          </PageLoading>


          <!-- AUTOMATED EMAILS TABLE -->
          <PageLoading v-else-if="routeName === 'automatedEmails'"
                       :loading="loading.pastEvents || loading.liveEvents || loading.calendarEvents"
                       pageName="Automated Emails"
          >
            <AutomatedEmailsTable
              :emailData="automatedEmailData"
            >
            </AutomatedEmailsTable>
          </PageLoading>

          <PageLoading v-else-if="routeName === 'revenue'"
                       :loading="loading.revenue"
                       pageName="Revenue Metrics"
          >
            <RevenueMetrics
              :metricsCards="newMetricsCards"
            />
          </PageLoading>

          <PageLoading v-else-if="routeName === 'revenue-settings'"
                       :loading="loading.revenueSettings"
                       pageName="Revenue Settings"
          >
            <RevenueSettings :settings="metricsSettings.settings" />
          </PageLoading>

          <PageLoading v-else-if="routeName === 'revenue-filter'"
                       :loading="loading.revenueSettings || loading.subscriptionProducts"
                       pageName="Revenue Filter"
          >
            <RevenueFilter :filter="metricsSettings.filter" />
          </PageLoading>

          <PageLoading v-else-if="routeName === 'revenue-customer-lists'"
                       :loading="loading.revenueSettings"
                       pageName="Customer Lists"
          >
            <RevenueCustomerLists
              :excluded="metricsSettings.excludedCustomers"
              :overridden="metricsSettings.overriddenCustomers"
            />
          </PageLoading>

          <PageLoading v-else-if="routeName === 'revenue-charts'"
                       :loading="loading.revenue || loading.profile"
                       pageName="Revenue Charts"
          >
            <PieChart
              :metricsCharts="newMetricsCharts"
            />
          </PageLoading>

          <PageLoading v-else-if="routeName === 'revenue-projections'"
                       :loading="loading.revenue || loading.profile"
                       pageName="Revenue Projections"
          >
            <RevenueProjections
              :defaultValues="newMetricsProjections"
            />
          </PageLoading>

          <!-- TIME METRICS -->
          <PageLoading v-else-if="routeName === 'time'"
                       :loading="loading.time || loading.profile"
                       pageName="Time"
          >
            <TimeMetrics
              :timeData="timeData" />
          </PageLoading>

          <!-- CUSTOMERS -->
          <PageLoading v-else-if="routeName === 'customers'"
                       :loading="loading.customers"
                       pageName="Customers"
          >
            <Customers
              :customerMetrics="customerMetrics"
              :customers="customers" />
          </PageLoading>

          <!-- HISTORICAL DATA -->
          <PageLoading v-else-if="routeName === 'historical'"
                       :loading="loading.historicalMetrics"
                       pageName="Historical Metrics">
            <HistoricalMetrics :historicalMetricsData="historicalMetrics" />
          </PageLoading>

          <PageLoading v-else-if="routeName === 'historical-details'"
                       :loading="loading.historicalMetrics"
                       pageName="Historical Metrics Details">
            <HistoricalMetricsDetails :historicalMetricsData="historicalMetrics" />
          </PageLoading>

          <!-- DELINQUENT CUSTOMERS -->
          <PageLoading v-else-if="routeName === 'customers-delinquent'"
                       :loading="loading.delinquentCustomers || loading.profile"
                       pageName="Delinquent Customers"
          >
            <DelinquentCustomers
              :delinquentCustomers="delinquentCustomers" />
          </PageLoading>


          <!-- RECOVERED CUSTOMERS -->
          <PageLoading v-else-if="routeName === 'customers-recovered'"
                       :loading="loading.recoveredCustomers || loading.profile"
                       pageName="Recovered Customers"
          >
            <RecoveredCustomers
              :recoveredCustomers="recoveredCustomers" />
          </PageLoading>

          <!-- Email Campaigns List -->
          <PageLoading v-else-if="routeName === 'email-campaigns'"
                       :loading="loading.emailCampaigns"
                       pageName="Email Campaigns">
            <EmailCampaigns :email-campaigns="emailCampaigns" />
          </PageLoading>

          <!-- Email Campaign Details -->
          <PageLoading v-else-if="routeName === 'email-campaign-details'"
                       :loading="loading.emailCampaigns"
                       pageName="Email Campaign Details">
            <EmailCampaignDetails :id="$route.params.id"
                                  :email-campaigns="emailCampaigns"
                                  :email-campaigns-steps="emailCampaignSteps" />
          </PageLoading>

          <!-- Create Email Campaign -->
          <CreateEmailCampaign v-else-if="routeName === 'create-email-campaign'" />

          <!-- EXPIRING CARDS -->
          <PageLoading v-else-if="routeName === 'expiring-cards'"
                       :loading="loading.expiringCards"
                       pageName="Expiring Cards"
          >
            <ExpiringCards :expiringCards="expiringCards" />
          </PageLoading>


          <!-- PAYMENT COLLECTION -->
          <PageLoading v-else-if="routeName === 'payment-collection-form'"
                       :loading="loading.paymentCollection"
                       pageName="Payment Collection Form"
          >
            <PaymentCollectionForm
              :paymentCollectionData="paymentCollectionData"
              :sampleFormData="sampleFormData" />
          </PageLoading>

          <!-- PAYMENT COLLECTION SNIPPET -->
          <PageLoading v-else-if="routeName === 'payment-collection-snippet'"
                       :loading="loading.paymentCollectionSnippet"
                       pageName="Payment Collection Snippet">
            <PaymentCollectionSnippet></PaymentCollectionSnippet>
          </PageLoading>


          <!-- PROFILE -->
          <PageLoading v-else-if="routeName === 'profile'"
                       :loading="loading.profile || loading.paymentInfo"
                       pageName="Profile">
            <Profile
              :paymentInfo="paymentInfo"
              :profileData="profileData" />
          </PageLoading>


          <!-- INTEGRATIONS -->
          <PageLoading v-else-if="routeName === 'integrations'"
                       :loading="loading.integrations"
                       pageName="Integrations"
          >
            <Integrations :integrations="integrations" />
          </PageLoading>


          <!-- TEAM -->
          <PageLoading v-else-if="routeName === 'team'"
                       :loading="loading.team"
                       pageName="Team"
          >
            <Team
              :teamMembers="teamMembers" />
          </PageLoading>


          <!-- REFERRALS -->
          <PageLoading v-else-if="routeName === 'referrals'"
                       :loading="loading.referrals"
                       pageName="Referrals">
            <Referrals :referralsInfo="referrals" />
          </PageLoading>

          <!-- STRIPE CONNECT AUTH: SETUP -->
          <StripeAuth v-else-if="routeName === 'stripe-auth'"
                      @refresh="fetchData">
          </StripeAuth>

          <!-- STRIPE CONNECT AUTH: PROFILE -->
          <!--
          <StripeAuth
            :profile="true"
            @refresh="refetchStripeData"
            v-else-if="routeName === 'stripe-auth-profile'">
          </StripeAuth>
          -->

          <!-- GOOGLE CALENDAR AUTH OR GMAIL AUTH -->
          <PageLoading v-else-if="routeName === 'google-calendar-auth' || routeName === 'gmail-auth'"
                       :loading="loading.userInfo">
            <GoogleAuth v-if="userInfo && userInfo.user" :userId="userInfo.user.id" />
          </PageLoading>

          <PageLoading v-else-if="routeName === 'notice'"
                       :loading="loading.paymentInfo"
                       pageName="Notice">
            <Notice :paymentInfo="paymentInfo" />
          </PageLoading>

          <PageLoading v-else-if="routeName === 'payment'"
                       :loading="loading.paymentInfo"
                       pageName="Payment">
            <Payment :paymentInfo="paymentInfo" />
          </PageLoading>
          <router-view v-else />
        </NavWrapper>
      </div>

      <div v-else :class="[{'pk-wrapper': (routeName !== 'home' ||
        (routeName === 'home' && loading.login))}]">
        <Loading v-if="(routeName === 'login' || isLandingPageRoute ) && loading.login" />

        <Loading v-else-if="(routeName === 'register' || routeName === 'referral-link' || routeName === 'team-registration')
          && loading.login"
                 message="Signing You Up" />

        <Login v-else-if="routeName === 'login'"
               @login="goToAccountAfterLogin"
        />

        <Signup v-else-if="routeName === 'register'"
                @signup="goToAccountAfterLogin"
        />

        <RequestPasswordReset v-else-if="routeName === 'request-password-reset-link'" />

        <ResetPassword v-else-if="routeName === 'reset-password'" />

        <UpdatePayment v-else-if="routeName === 'update-payment'"
                       :sampleFormData="sampleFormData" />

        <StripeFormSuccess v-else-if="routeName === 'checkout-success'"
        />

        <StripeFormCancel v-else-if="routeName === 'checkout-cancel'"
        />

        <LandingPage v-else-if="isLandingPageRoute"
                     @fetch="goToAccount"
        />

        <ReferralLink v-else-if="routeName === 'referral-link'"
                      @signup="goToAccountAfterLogin"
        />

        <TeamRegistration v-else-if="routeName === 'team-registration'"
                          @signup="goToAccountAterTeamSignup()"
        />

        <router-view v-else />
      </div>
    </v-app>
  </div>
</template>

<script>
// import * as typeformEmbed from '@typeform/embed'

import LoginService from './api/login-and-signup';
import { EventBus } from './event-bus';
import CalendarDailyMixin from './mixins/CalendarDaily';
import EmailTableHelper from './mixins/EmailTableHelper';
import Intercom from './mixins/Intercom';
/* Mixins */
import ParseData from './mixins/ParseData';

export default {
  name: 'App',
  components: {
    /* Lazy load components */
    Alert: () => import('./components/save-changes/Alert.vue'),
    Header: () => import('./components/header/Header.vue'),
    NavWrapper: () => import('./components/NavWrapper.vue'),

    CalendarView: () => import('./views/CalendarView.vue'),
    CalendarDaily: () => import('./components/calendar-expanded/CalendarExpandedView.vue'),
    CashflowCharts: () => import('./components/cashflow-charts/CashflowCharts.vue'),
    Exports: () => import('./components/exports/Exports.vue'),
    CalendarEmails: () => import('./components/calendar-emails/CalendarEmails.vue'),
    LiveEvents: () => import('./components/live-events/LiveEvents.vue'),
    AutomatedEmailsTable: () => import('./components/email-addons/EmailTable.vue'),
    RevenueMetrics: () => import('./components/new-metrics/RevenueMetrics.vue'),
    RevenueSettings: () => import('./components/new-metrics/settings/All.vue'),
    RevenueFilter: () => import('./components/new-metrics/other-settings/Filter.vue'),
    RevenueCustomerLists: () => import('./components/new-metrics/customers/Customers.vue'),
    RevenueProjections: () => import('./components/new-metrics/projections/All.vue'),
    PieChart: () => import('./components/new-metrics/pie-charts/All.vue'),
    TimeMetrics: () => import('./components/metrics/TimeMetrics.vue'),
    Customers: () => import('./components/customers/CustomersTable.vue'),
    HistoricalMetrics: () => import('./components/historical-metrics/HistoricalMetrics.vue'),
    HistoricalMetricsDetails: () => import('./components/historical-metrics/HistoricalMetricsDetails.vue'),
    DelinquentCustomers: () => import('./components/customers/DelinquentCustomersTable.vue'),
    RecoveredCustomers: () => import('./components/customers/RecoveredCustomersTable.vue'),
    EmailCampaigns: () => import('./components/email-campaigns/EmailCampaignsPage.vue'),
    EmailCampaignDetails: () => import('./components/email-campaigns/EmailCampaignDetailsPage.vue'),
    CreateEmailCampaign: () => import('./components/email-campaigns/CreateEmailCampaignPage.vue'),
    ExpiringCards: () => import('./components/customers/ExpiringCards.vue'),
    PaymentCollectionForm: () => import('./components/payment-collection/PaymentCollectionForm.vue'),
    PaymentCollectionSnippet: () => import('./components/payment-collection-snippet/PaymentCollectionSnippetPage.vue'),

    Profile: () => import('./components/profile/Profile.vue'),
    Integrations: () => import('./components/integrations/Integrations.vue'),
    Team: () => import('./components/team/Team.vue'),
    Referrals: () => import('./components/referrals/Referrals.vue'),

    StartTrial: () => import('./components/screens/StartTrial.vue'),
    Notice: () => import('./components/screens/Notice.vue'),
    Payment: () => import('./components/screens/Payment.vue'),

    StripeConnect: () => import('./components/onboarding/StripeConnect.vue'),
    DownloadingStripeData: () => import('./components/onboarding/DownloadingStripeData.vue'),
    StripeAuth: () => import('./components/onboarding/StripeAuth.vue'),
    GoogleAuth: () => import('./components/onboarding/GoogleAuth.vue'),

    Login: () => import('./components/login/Login.vue'),
    Signup: () => import('./components/login/Signup.vue'),
    RequestPasswordReset: () => import('./components/login/RequestPasswordResetLink.vue'),
    ResetPassword: () => import('./components/login/ResetPassword.vue'),
    ReferralLink: () => import('./components/login/ReferralSignup.vue'),
    TeamRegistration: () => import('./components/login/TeamSignup.vue'),

    /* Outside Pages */
    LandingPage: () => import('./components/landing-page/LandingPage.vue'),
    UpdatePayment: () => import('./components/payment-collection/UpdatePayment.vue'),
    StripeFormSuccess: () => import('./components/payment-collection/StripeFormSuccess.vue'),
    StripeFormCancel: () => import('./components/payment-collection/StripeFormCancel.vue'),

    Loading: () => import('./components/loading/Loading.vue'),
    PageLoading: () => import('./components/loading/PageLoading.vue'),
  },
  mixins: [
    ParseData,
    CalendarDailyMixin,
    EmailTableHelper,
    Intercom,
  ],
  data() {
    return {};
  },
  mounted() {
    // *** Start Intercom ***
    this.launchIntercom();
    /* Destroy timer to be safe */
    this.destroyTimer();
    EventBus.$on('setGlobalState', (key, value) => {
      this.setGlobalState(key, value);
    });
    EventBus.$on('callGlobalFunction', (funcName, params = []) => {
      if (this[funcName] && typeof this[funcName] === 'function') {
        this[funcName](...params);
      }
    });

    // *** CHECK AUTH STATUS FROM API
    LoginService.checkAuth().then((results) => {
      const userAuth = results.data;
      // *** IF NOT LOGGED IN ON BACKEND SESSION, DESTROY FRONTEND SESSION + REDIRECT TO /LOGIN
      if (!userAuth || !userAuth.user) {
        // Destroy session + redirect to log in
        this.$store.dispatch('logOut');
        if (this.$route.meta.requiresAuth) {
          this.$router.push({ name: 'login' });
        }
      }
      // *** IF LOGGED IN ON BACKEND SESSION ***
      else {
        this.startSession(userAuth);
        this.parseUserInfo(userAuth);
        this.fetchData();
      }
    }).catch(err => {
      this.$store.dispatch('logOut');
      if (this.$route.meta.requiresAuth) {
        this.$router.push({ name: 'login' });
      }
    });

    /* Confirm message when exiting the tab */
    window.onbeforeunload = (e) => {
      if (this.$store.getters.getTempVal('isDirty')) {
        /* eslint-disable */
        e = e || window.event;

        // For IE and Firefox prior to version 4
        if (e) {
          e.returnValue = 'Sure?';
        }

        // For Safari
        return 'Sure?';
      }

      return;
    };
  },
  destroyed() {
    /* Destroy timer to be safe */
    this.destroyTimer();
  },
  methods: {
    goToAccount() {
      this.$router.push({ name: 'calendar' });
    },
    goToAccountAfterLogin() {
      this.loading.login = true;
      this.fetchData(
        true,
        true,
        '',
        true,
      );
    },
    goToAccountAterTeamSignup() {
      this.teamAllRecomputedData(true, {}, true);
    },
  },
  computed: {
    requiresAuth() {
      return this.$route.meta.requiresAuth;
    },
    routeName() {
      return this.$route.name;
    },
    isLandingPageRoute() {
      const landingPageRoutes = {
        home: true,
        about: true,
        contact: true,
        privacy: true,
        terms: true,
        feedback: true,
        changelog: true,
        testimonials: true,
        'resources-revenue-metrics-export': true,
        'resources-revenue-metrics-export-success': true,
      };
      return landingPageRoutes[this.routeName];
    },
    isCalendarEmailRoute() {
      const calendarEmailRoutes = {
        'upcomingEvents': true,
        'calendarLiveEvents': true,
        'pastEvents': true,
      };
      return calendarEmailRoutes[this.routeName];
    },
  },
};
</script>

<style lang='scss'>

/* Load in styles */
@import "./styles/layout/branding.scss";
@import "./styles/layout/buttons.scss";
@import "./styles/layout/card-centered.scss";
@import "./styles/layout/tip.scss";

html, body, .application, .pk-wrapper-container {
  height: 100%;
  width: 100%;
  overflow: hidden;
}

#intercom-container :focus, #intercom-container :focus::after {
  outline-color: transparent !important;
}

.application {
  position: relative;
}

.application--wrap {
  overflow: hidden;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
  background-color: white;
}

.pk-auth-wrapper, .pk-wrapper {
  height: 100%;
  width: 100%;
  overflow: hidden;
  background-color: white;
}

.theme--light.v-text-field:not(.error--text) > .v-input__control > .v-input__slot:before {
  border-color: #c3c3c3 !important;
}

b {
  display: inline;
}

.v-list__tile {
  height: none;
  min-height: 48px;
}


@mixin app-400px {
  @media (max-width: 400px) {
    @content;
  }
}

@include app-400px {
  .v-dialog {
    margin: 0 !important;
  }
}


</style>
